"use client";
import React, { Fragment } from "react";
import Image, { StaticImageData } from "next/image";
import {
  Box,
  Center,
  Grid,
  GridItem,
  Heading,
  HStack,
  Text,
  Stack,
} from "@chakra-ui/react";
import Tag from "../tag";
import {
  useSectionHeadingVariant,
  useTextVariants,
} from "@/theme/components/useSectionHeading";
import TickImage from "public/shared/blueCheck.jpeg";

type IntroProps = {
  children?: React.ReactNode;
  description?: React.ReactNode;
  conclusion?: string;
  heading?: string;
  image: StaticImageData;
  title?: string;
  points?: string[];
  imageHeight?: string;
};

const Intro = (props: IntroProps) => {
  const {
    children,
    description,
    heading,
    image,
    points,
    title,
    conclusion,
    imageHeight = "400px",
  } = props;
  const HeadingVariant = useSectionHeadingVariant();
  const TextVariant = useTextVariants();
  return (
    <Grid
      gap={4}
      templateColumns={{
        base: "repeat(1, 1fr)",
        md: "repeat(5, 1fr)",
      }}
    >
      <GridItem colSpan={{ base: 1, md: 3 }}>
        <Stack spacing={4}>
          {heading && <Tag label={heading} />}
          {title && (
            <Heading as="h1" variant={HeadingVariant} marginTop="10px">
              {title}
            </Heading>
          )}
          {description && (
            <Fragment>
              {typeof description === "string" ? (
                <Text
                  variant={TextVariant}
                  color="text.100"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              ) : Array.isArray(description) ? (
                <Stack spacing={2}>
                  {description.map((desc, index) => (
                    <Text key={index} variant={TextVariant} color="text.100">
                      {desc}
                    </Text>
                  ))}
                </Stack>
              ) : (
                description
              )}
            </Fragment>
          )}
          {points && (
            <Stack color="text.100">
              {points.map((point, index) => (
                <HStack key={index} align="start">
                  <Image
                    alt={"Point"}
                    src={TickImage}
                    style={{ height: "auto", width: "20px", paddingTop: "4px" }}
                  />{" "}
                  <Text variant={TextVariant} fontWeight={550}>
                    {point}
                  </Text>
                </HStack>
              ))}
            </Stack>
          )}
          {children && children}
          {conclusion && (
            <Text variant={TextVariant} color="text.100" fontWeight={600}>
              {conclusion}
            </Text>
          )}
        </Stack>
      </GridItem>
      <GridItem colSpan={{ base: 1, md: 2 }}>
        <Center height={"full"}>
          <Box
            h={{ base: "250px", sm: imageHeight }}
            w={{ base: "100%", sm: "80%", md: "500px" }}
            position="relative"
            alignSelf={"center"}
          >
            <Image
              src={image}
              alt="IntroImg"
              layout="fill"
              objectFit="contain"
              objectPosition="center"
              style={{ borderRadius: "32px" }}
            />
          </Box>
        </Center>
      </GridItem>
    </Grid>
  );
};

export default Intro;
